import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { useGetPermitFiltersOptions } from '@src/permits/hooks/useGetPermitFiltersOptions';
import { BillToClientFilter } from '@src/permits/permitSearch/filters/BillToClientFilter';
import { ClaimedByFilter } from '@src/permits/permitSearch/filters/ClaimedByFilter';
import { InvoiceNumberFilter } from '@src/permits/permitSearch/filters/InvoiceNumberFilter';
import { OrderClientFilter } from '@src/permits/permitSearch/filters/OrderClientFilter';
import { PermitJurisdictionFilter } from '@src/permits/permitSearch/filters/PermitJurisdictionFilter';
import { PermitNameFilter } from '@src/permits/permitSearch/filters/PermitNameFilter';
import { PermitTypeFilter } from '@src/permits/permitSearch/filters/PermitTypeFilter';
import { StateIdFilter } from '@src/permits/permitSearch/filters/StateIdFilter';
import { StatusFilter } from '@src/permits/permitSearch/filters/StatusFilter';
import { SubmissionDateFilter } from '@src/permits/permitSearch/filters/SubmissionDateFilter';
import { TractorLicensePlateNumberFilter } from '@src/permits/permitSearch/filters/TractorLicensePlateNumberFilter';
import { TractorUnitNumberFilter } from '@src/permits/permitSearch/filters/TractorUnitNumberFilter';
import { TractorVINFilter } from '@src/permits/permitSearch/filters/TractorVINFilter';
import type { SearchPermitForReportFilters } from '@src/permits/services/PermitApplicationClient';

interface Props {
  filters: SearchPermitForReportFilters;
  onFilterChange: (filters: SearchPermitForReportFilters) => void;
}

export const FiltersForReportSearch = ({ onFilterChange, filters }: Props) => {
  const { options } = useGetPermitFiltersOptions();
  if (!filters) {
    return null;
  }
  const getClientsOptions = (clients: string[]) => {
    return clients
      .map((clientId) => {
        const client = options?.clients.find((c) => c.id === clientId);
        return client
          ? {
              name: client.name,
              value: client.id,
            }
          : null;
      })
      .filter(
        (option): option is { name: string; value: string } => option !== null,
      );
  };
  const selectedOrderClientOptions = getClientsOptions(filters.client || []);
  const selectedBillToClientOptions = getClientsOptions(filters.billTo || []);

  const width = '280px';

  return (
    <Stack gap={theme.spacing['5']}>
      <Stack direction="row" gap={theme.spacing['5']} flexWrap="wrap">
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <SubmissionDateFilter
            onFilterChange={onFilterChange}
            value={filters.submissionDate}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <PermitJurisdictionFilter
            onFilterChange={onFilterChange}
            value={filters.jurisdiction || []}
            options={options?.jurisdictions}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <PermitTypeFilter
            onFilterChange={onFilterChange}
            value={filters.type || []}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <PermitNameFilter
            onFilterChange={onFilterChange}
            value={filters.permitName}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <OrderClientFilter
            onFilterChange={onFilterChange}
            value={selectedOrderClientOptions}
            options={options?.clients}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <StateIdFilter
            onFilterChange={onFilterChange}
            value={filters.stateId}
          />
        </Stack>
      </Stack>

      <Stack direction="row" gap={theme.spacing['5']} flexWrap="wrap">
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <TractorLicensePlateNumberFilter
            onFilterChange={onFilterChange}
            value={filters.tractorLicensePlateNumber}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <TractorUnitNumberFilter
            onFilterChange={onFilterChange}
            value={filters.tractorUnitNumber}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <TractorVINFilter
            onFilterChange={onFilterChange}
            value={filters.tractorVIN}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <InvoiceNumberFilter
            onFilterChange={onFilterChange}
            value={filters.invoiceNumber}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <BillToClientFilter
            onFilterChange={onFilterChange}
            value={selectedBillToClientOptions}
            options={options?.clients}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <StatusFilter
            onFilterChange={onFilterChange}
            value={filters.status || []}
            options={options?.statuses}
          />
        </Stack>
        <Stack flexGrow={0} flexShrink={1} width={width}>
          <ClaimedByFilter
            onFilterChange={onFilterChange}
            value={filters.claimedBy}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
